import { Turn } from '../types/ski-data';
import { TurnType } from '../types/enums';
import { COACH_FORBIDDEN_WORDS } from '../constants/algorithm-params';

/**
 * Coach narrative templates (FR-04 教练话术).
 *
 * Generates natural-language skiing coaching feedback from structured `Turn`
 * diagnostics. Narratives follow a three-segment structure:
 *
 *   1. What — describe what happened in this turn (diagnosis).
 *   2. Why  — causal analysis of the root cause.
 *   3. How  — actionable improvement suggestion for next run.
 *
 * All output is filtered through `COACH_FORBIDDEN_WORDS` (no preachy words like
 * "建议" / "请" / "您"), keeping the tone of a peer coach rather than a nagging
 * instructor.
 *
 * Related requirements:
 *   - FR-04 教练话术 (this module)
 *   - FR-11 趟总结     — `generateRunSummary`
 *   - FR-12 受限诊断问答 — `PRESET_QUESTIONS` / `generateAnswer`
 */

/**
 * Generate a full coach narrative for a single turn (FR-04).
 * Returns the concatenated What → Why → How segments, with forbidden words
 * stripped.
 */
export function generateTurnNarrative(turn: Turn): string {
  const segments: string[] = [];

  // What - describe what happened
  segments.push(generateWhatSegment(turn));
  // Why - causal analysis
  segments.push(generateWhySegment(turn));
  // How - improvement suggestion (without forbidden words)
  segments.push(generateHowSegment(turn));

  const narrative = segments.join('');

  // Filter forbidden words
  return filterForbiddenWords(narrative);
}

/** What segment: factual description of the turn. */
function generateWhatSegment(turn: Turn): string {
  const turnNum = turn.turn_index;
  const direction = turn.direction === 'Left' ? '左' : '右';

  switch (turn.turn_type) {
    case TurnType.Carving:
      return `第${turnNum}个弯是个${direction}转卡宾，立刃到${Math.round(
        turn.edge_angle_avg
      )}度，压力干净地转到外板，卡出了漂亮的弧线。`;
    case TurnType.Edging:
      if (turn.overall_score >= 75) {
        return `第${turnNum}个弯是个${direction}转走刃，立刃${Math.round(
          turn.edge_angle_avg
        )}度，外板占比到了${(turn.outside_pressure_ratio * 100).toFixed(0)}%。`;
      }
      return `第${turnNum}个弯是个${direction}转走刃，立刃只有${Math.round(
        turn.edge_angle_avg
      )}度，外板占比${(turn.outside_pressure_ratio * 100).toFixed(0)}%偏低。`;
    case TurnType.Skidding:
      if (turn.outside_pressure_ratio < 0.6) {
        return `第${turnNum}个弯${direction}转搓出去了，立刃只有${Math.round(
          turn.edge_angle_avg
        )}度，外板占比${(turn.outside_pressure_ratio * 100).toFixed(
          0
        )}%，压力没来得及转到外板。`;
      }
      return `第${turnNum}个弯${direction}转搓雪了，立刃${Math.round(turn.edge_angle_avg)}度偏浅。`;
    default:
      return '';
  }
}

/** Why segment: causal root-cause analysis. */
function generateWhySegment(turn: Turn): string {
  if (turn.turn_type === TurnType.Skidding) {
    if (turn.outside_pressure_ratio < 0.6 && turn.edge_angle_avg < 18) {
      return `入弯太急，重心还没压到外侧板就转了，立刃起不来，板底蹭雪自然搓出去。`;
    }
    if (turn.outside_pressure_ratio < 0.6) {
      return `压力转移慢了，弯中段外板还没吃住力，板尾打滑。`;
    }
    return `立刃角度不够，板底没法刻住雪面。`;
  }
  if (turn.turn_type === TurnType.Edging && turn.edge_angle_avg < 18) {
    return `立刃偏浅，离25度卡宾门槛还有差距。`;
  }
  if (turn.edging_sync_score < 70) {
    return `左右板立刃不同步，偏移${Math.round(100 - turn.edging_sync_score)}分。`;
  }
  return `弯形和节奏都不错。`;
}

/** How segment: actionable improvement suggestion. */
function generateHowSegment(turn: Turn): string {
  if (turn.turn_type === TurnType.Skidding && turn.outside_pressure_ratio < 0.6) {
    return `下次入弯时把重心早一点压到外侧板上，等外板吃住力再转。`;
  }
  if (turn.turn_type === TurnType.Skidding) {
    return `多练立刃，先把角度顶到20度以上。`;
  }
  if (turn.edge_angle_avg < 18) {
    return `继续加深立刃，目标是25度以上进卡宾。`;
  }
  if (turn.edging_sync_score < 70) {
    return `注意双板同时立刃，别一先一后。`;
  }
  return `保持这个节奏，可以试试更大的立刃角度。`;
}

/** Remove all forbidden words from `text` (see `COACH_FORBIDDEN_WORDS`). */
function filterForbiddenWords(text: string): string {
  let result = text;
  for (const word of COACH_FORBIDDEN_WORDS) {
    result = result.replaceAll(word, '');
  }
  return result;
}

/**
 * Generate a run summary narrative (FR-11 趟总结).
 *
 * Summarizes the whole run: turn count, average score, common issues, and the
 * worst turns, ending with a focus point for next time.
 */
export function generateRunSummary(turns: Turn[]): string {
  if (turns.length === 0) return '这趟没有记录到弯。';

  // Statistics
  const typeCounts = { Carving: 0, Edging: 0, Skidding: 0 };
  for (const t of turns) typeCounts[t.turn_type]++;

  const scores = turns.map((t) => t.overall_score);
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;

  // Find worst turns
  const sorted = [...turns].sort((a, b) => a.overall_score - b.overall_score);
  const worstTwo = sorted.slice(0, 2).map((t) => t.turn_index);

  // Main issue diagnosis
  const shallowCount = turns.filter((t) => t.edge_angle_avg < 20).length;
  const pressureIssueCount = turns.filter((t) => t.outside_pressure_ratio < 0.6).length;

  let mainIssue = '';
  if (pressureIssueCount > shallowCount) {
    mainIssue = '压力转移';
  } else if (shallowCount > 0) {
    mainIssue = '立刃偏浅';
  } else {
    mainIssue = '整体表现稳定';
  }

  let summary = `这趟${turns.length}个弯，平均分${avgScore.toFixed(0)}。`;

  if (shallowCount > 0) {
    summary += `${turns.length}个弯里有${shallowCount}个立刃低于20度。`;
  }

  if (worstTwo.length > 0) {
    summary += `最差的是第${worstTwo.join('和第')}个弯`;
    const worst = sorted[0];
    if (worst.outside_pressure_ratio < 0.6) {
      summary += '，都是入弯太急导致压力没转过来';
    } else if (worst.edge_angle_avg < 18) {
      summary += '，立刃太浅';
    }
    summary += '。';
  }

  if (mainIssue === '压力转移') {
    summary += '下次重点练入弯时的压力转移，把重心早一点压到外侧板上。';
  } else if (mainIssue === '立刃偏浅') {
    summary += '下次重点练立刃深度，目标25度以上。';
  }

  return filterForbiddenWords(summary);
}

/**
 * Preset questions for FR-12 受限诊断问答.
 * The diagnostic panel exposes exactly these three questions per turn.
 */
export const PRESET_QUESTIONS = [
  { id: 'q1', question: '为什么这个弯搓出去了？' },
  { id: 'q2', question: '和上一个弯差在哪？' },
  { id: 'q3', question: '下次重点练什么？' },
] as const;

/**
 * Generate an answer for a preset question (FR-12 受限诊断问答).
 *
 * @param questionId   - One of `'q1' | 'q2' | 'q3'` (see `PRESET_QUESTIONS`).
 * @param currentTurn  - The currently selected turn, or `null` if none.
 * @param allTurns     - All turns in the run (used for `q2` comparison).
 */
export function generateAnswer(
  questionId: string,
  currentTurn: Turn | null,
  allTurns: Turn[]
): string {
  if (!currentTurn) {
    return '请先在弯列表选择一个弯。';
  }

  switch (questionId) {
    case 'q1': {
      // Why did this turn skid?
      if (currentTurn.turn_type !== TurnType.Skidding) {
        return `这个弯没有搓雪，判定为${
          currentTurn.turn_type === TurnType.Carving ? '卡宾' : '走刃'
        }，评分${currentTurn.overall_score.toFixed(0)}。`;
      }
      const reasons: string[] = [];
      if (currentTurn.outside_pressure_ratio < 0.6) {
        reasons.push(
          `外板占比峰值仅${(currentTurn.outside_pressure_ratio * 100).toFixed(0)}%，低于0.75达标线`
        );
      }
      if (currentTurn.edge_angle_avg < 18) {
        reasons.push(
          `立刃均值${currentTurn.edge_angle_avg.toFixed(0)}度，低于25度卡宾门槛`
        );
      }
      if (currentTurn.edging_sync_score < 70) {
        reasons.push(`双板同步率${currentTurn.edging_sync_score.toFixed(0)}，存在时序偏移`);
      }
      return `这个弯搓雪的原因：${reasons.join('，')}。`;
    }
    case 'q2': {
      // Compare with previous turn
      const prevTurn = allTurns.find((t) => t.turn_index === currentTurn.turn_index - 1);
      if (!prevTurn) {
        return `这是第1个弯，没有上一个弯可对比。`;
      }
      const scoreDiff = currentTurn.overall_score - prevTurn.overall_score;
      const edgeDiff = currentTurn.edge_angle_avg - prevTurn.edge_angle_avg;
      const pressureDiff =
        currentTurn.outside_pressure_ratio - prevTurn.outside_pressure_ratio;

      return `和第${prevTurn.turn_index}个弯对比：评分${
        scoreDiff > 0 ? '高' : '低'
      }了${Math.abs(scoreDiff).toFixed(0)}分，立刃${edgeDiff > 0 ? '深' : '浅'}了${Math.abs(
        edgeDiff
      ).toFixed(0)}度，外板占比${pressureDiff > 0 ? '高' : '低'}了${Math.abs(
        pressureDiff * 100
      ).toFixed(0)}%。`;
    }
    case 'q3': {
      // What to practice next
      if (currentTurn.outside_pressure_ratio < 0.6) {
        return `重点练入弯时的压力转移，把重心早一点压到外侧板上，目标是外板占比到75%。`;
      }
      if (currentTurn.edge_angle_avg < 20) {
        return `重点练立刃深度，当前${currentTurn.edge_angle_avg.toFixed(0)}度，目标25度以上。`;
      }
      if (currentTurn.edging_sync_score < 70) {
        return `重点练双板同步立刃，别一先一后。`;
      }
      return `这个弯质量不错，可以试试更大的立刃角度和更圆的弯形。`;
    }
    default:
      return '问题未识别。';
  }
}
